import React, { memo, useRef } from 'react';
import {
  EDGES, EDGE_NORMAL, Edge, MoveKind, PUZZLE_HALF_SIZE, SUBGRID_HALF_SIZE, SubgridState,
  apply, childOffset, compose, continueAngle, inverse, orientationElement, toCss,
} from '../geometry';
import { flipQuadrantByEdge, flipSubgridByCell, rotateSubgrid } from '../ultrarubigramUtils';
import RotateButton from './RotateButton';

type SubgridProps = {
  g: number;
  q: number;
  s: number;
  subgrid: SubgridState;
  /** Screen orientation of the quadrant this subgrid sits in. */
  parent: number;
  /** Screen position of that quadrant's center, in half-cells from the puzzle center. */
  parentCenterX: number;
  parentCenterY: number;
  interactive: boolean;
  /**
   * Instead of the tile itself, render one of the copies of it that other layers are made
   * of: its border bands (see Board), or its quarters of the 6x6 flip tabs on the 6x6 seams
   * and outer edges (see Grid) or on the 12x12 seams (see Board).
   */
  layer?: SubgridLayer;
  /** The move the quadrant or grid this tile sits in is making, if any (the nearest one). */
  ancestorMoving: MoveKind | null;
};

export type SubgridLayer = 'bands' | 'innerTabs' | 'seamTabs';

/**
 * How much thicker than its own 2px border a tile's side must be, given where that side lies
 * on screen (in half-cells from the puzzle center): seams between 6x6s are 4px, seams between
 * 12x12s and the outside are 5px.
 */
const frameExtra = (position: number): number => {
  const distance = Math.abs(position);
  if (distance % 12 === 6) return 0; // between two 3x3s of one 6x6
  if (distance === 12) return 2; // between two 6x6s
  return 3; // between two 12x12s (0) or the outside (24)
};

/**
 * A tile's four corners in its tab frame (see .ultraQuarterFrame), with the two sides that meet
 * at each. Each corner is either the quadrant's center, one of its corners, or the midpoint of
 * one of its edges; the last kind carries a quarter of that edge's flip tab.
 */
type Corner = { name: 'tl' | 'tr' | 'bl' | 'br'; x: -1 | 1; y: -1 | 1; sides: [Edge, Edge] };
const CORNERS: Corner[] = [
  { name: 'tl', x: -1, y: -1, sides: ['top', 'left'] },
  { name: 'tr', x: 1, y: -1, sides: ['top', 'right'] },
  { name: 'bl', x: -1, y: 1, sides: ['bottom', 'left'] },
  { name: 'br', x: 1, y: 1, sides: ['bottom', 'right'] },
];

type Quarter = {
  /** The quadrant's edge, as seen on screen, whose midpoint the corner sits on. */
  edge: Edge;
  /** Which of the corner's two sides runs along that edge, in the tab frame. */
  edgeSide: Edge;
  /** What lies across the edge: another 6x6 of the same 12x12, another 12x12, or nothing. */
  seam: 'quad' | 'grid' | 'outer';
};

/** The tab quarter on a corner of a tile, if any. `frame` maps the tab frame to the screen. */
const quarterAt = (
  corner: Corner, frame: number, centerX: number, centerY: number, quadrantX: number, quadrantY: number,
): Quarter | null => {
  const [ox, oy] = apply(frame, corner.x * SUBGRID_HALF_SIZE, corner.y * SUBGRID_HALF_SIZE);
  const dx = centerX + ox - quadrantX;
  const dy = centerY + oy - quadrantY;
  if ((dx === 0) === (dy === 0)) return null;
  const onSide = dy === 0; // on the left or right edge, rather than the top or bottom
  const edge: Edge = onSide ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'top' : 'bottom');
  const across = onSide ? centerX + ox : centerY + oy;
  const seam = Math.abs(across) === PUZZLE_HALF_SIZE ? 'outer' : across === 0 ? 'grid' : 'quad';
  const edgeSide = corner.sides.find(side => (apply(frame, ...EDGE_NORMAL[side])[0] !== 0) === onSide)!;
  return { edge, edgeSide, seam };
};

/**
 * Where a quarter sits relative to its tile's corner, so that the disc is centered on the middle
 * of the seam: 1px in along the side the tile shares with its neighbouring 3x3 (they overlap by
 * 2px), and on a 12x12 seam, where tiles are 1px apart, the right/bottom one reaches 1px out.
 * On the outside the disc's flat side lies on the puzzle's edge, i.e. the outer edge of the
 * band, so that it still looks part of the edge when the tile turns its band away from under
 * it (the disc is bigger by the same amount; see .ultraQuarter.outer). A hidden quarter keeps
 * a position so it never transitions from nowhere.
 */
const quarterStyle = (corner: Corner, quarter: Quarter | null): React.CSSProperties => {
  const style: Record<string, string> = {};
  for (const side of corner.sides) {
    if (!quarter) style[side] = '0px';
    else if (side !== quarter.edgeSide) style[side] = '1px';
    else if (quarter.seam === 'outer') style[side] = `${-frameExtra(PUZZLE_HALF_SIZE)}px`;
    else style[side] = quarter.seam === 'grid' && (quarter.edge === 'right' || quarter.edge === 'bottom') ? '-1px' : '0px';
  }
  return style as React.CSSProperties;
};

/** How far the band along the outside reaches beyond a tile's box; the most any frame does. */
const BAND_MARGIN = 3;

/**
 * The outline of a tile's band while a piece containing it moves (see .ultraContents::before):
 * a margin around the box wide enough for any frame, minus a bite at each corner that sits on
 * a flip tab, over the neighbouring quarter's split line, so that the band, drawn on top of
 * everything while it moves, never covers that line when at rest. The bite reaches `bite`
 * along the seam from the corner. Twelve vertices, three per corner, clockwise from the
 * top-left, so that every configuration has the same shape of polygon.
 */
const bandClip = (
  self: number, centerX: number, centerY: number, quadrantX: number, quadrantY: number, bite: string | null,
): string => {
  const out = `-${BAND_MARGIN}px`;
  const across = `calc(100% + ${BAND_MARGIN}px)`;
  const near = bite ?? '0px';
  const far = `calc(100% - ${near})`;
  const biteSide = (corner: Corner): Edge | null => {
    if (!bite) return null;
    const quarter = quarterAt(corner, self, centerX, centerY, quadrantX, quadrantY);
    return quarter && quarter.seam !== 'outer' ? quarter.edgeSide : null;
  };
  const [tl, tr, bl, br] = CORNERS.map(biteSide);
  const points = [
    ...(tl === 'top' ? [`${out} 0px`, `${near} 0px`, `${near} ${out}`]
      : tl === 'left' ? [`${out} ${near}`, `0px ${near}`, `0px ${out}`]
        : Array(3).fill(`${out} ${out}`)),
    ...(tr === 'top' ? [`${far} ${out}`, `${far} 0px`, `${across} 0px`]
      : tr === 'right' ? [`100% ${out}`, `100% ${near}`, `${across} ${near}`]
        : Array(3).fill(`${across} ${out}`)),
    ...(br === 'right' ? [`${across} ${far}`, `100% ${far}`, `100% ${across}`]
      : br === 'bottom' ? [`${across} 100%`, `${far} 100%`, `${far} ${across}`]
        : Array(3).fill(`${across} ${across}`)),
    ...(bl === 'bottom' ? [`${near} ${across}`, `${near} 100%`, `${out} 100%`]
      : bl === 'left' ? [`0px ${across}`, `0px ${far}`, `${out} ${far}`]
        : Array(3).fill(`${out} ${across}`)),
  ];
  return `polygon(${points.join(', ')})`;
};

const Subgrid = memo(({
  g, q, s, subgrid, parent, parentCenterX, parentCenterY, interactive, layer, ancestorMoving,
}: SubgridProps) => {
  const self = compose(parent, orientationElement(subgrid));
  const upright = inverse(self);

  const [offsetX, offsetY] = apply(parent, ...childOffset(s, SUBGRID_HALF_SIZE));
  const centerX = parentCenterX + offsetX;
  const centerY = parentCenterY + offsetY;

  const flipperStyle: React.CSSProperties = {
    transform: `rotateY(${subgrid.flipH ? 180 : 0}deg)`
      + ` rotateX(${subgrid.flipV ? 180 : 0}deg)`
      + ` rotateZ(${subgrid.rot}deg)`,
  };

  const pieceClasses = `ultraPiece ultraSubgrid pos${s}${subgrid.moving ? ' moving' : ''}`
    + (layer === 'bands' ? ' bands' : layer ? ' tabs' : '');
  const contentsClasses = 'ultraContents'
    + (subgrid.isFlipping ? ' isFlipping' : '')
    + (subgrid.moving ? ' isMoving' : '');

  const frameStyle: Record<string, string> = {};
  for (const edge of EDGES) {
    const [nx, ny] = apply(self, ...EDGE_NORMAL[edge]);
    const position = nx !== 0 ? centerX + nx * SUBGRID_HALF_SIZE : centerY + ny * SUBGRID_HALF_SIZE;
    frameStyle[`--frame${edge[0].toUpperCase()}${edge.slice(1)}`] = `${frameExtra(position)}px`;
  }

  if (layer === 'bands') {
    return (
      <div className={pieceClasses}>
        <div className="ultraFlipper" style={flipperStyle}>
          <div className={contentsClasses} style={frameStyle as React.CSSProperties}/>
        </div>
      </div>
    );
  }

  if (layer) {
    // The tab frame is the tile's frame without its own turning, so the quarters stay put on
    // screen while the tile turns under them and swing along with it only when it flips.
    const tabFrame = compose(parent, orientationElement({ rot: 0, flipH: subgrid.flipH, flipV: subgrid.flipV }));
    const tabFrameStyle: React.CSSProperties = { transform: `rotateZ(${-subgrid.rot}deg)` };

    return (
      <div className={pieceClasses}>
        <div className="ultraFlipper" style={flipperStyle}>
          <div className={contentsClasses}>
            <div className="ultraQuarterFrame" style={tabFrameStyle}>
              {CORNERS.map(corner => {
                const found = quarterAt(corner, tabFrame, centerX, centerY, parentCenterX, parentCenterY);
                const quarter = found && (found.seam === 'grid') === (layer === 'seamTabs') ? found : null;
                const classes = `ultraQuarter ${corner.name}` + (quarter
                  ? ` edge-${quarter.edge}${quarter.seam === 'outer' ? ' outer' : ` line-${quarter.edgeSide}`}`
                  : ' hidden');
                return (
                  <button
                    key={corner.name}
                    type="button"
                    className={classes}
                    style={quarterStyle(corner, quarter)}
                    onClick={quarter ? () => flipQuadrantByEdge(g, q, quarter.edge) : undefined}
                  >
                    <span className="disc"/>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Only a moving 6x6 or 12x12 is drawn above its neighbours' tabs (see .bigMoving), so only
  // then does the band need bites. While it flips, the bands along the flip axis foreshorten
  // in place over the neighbours' lines, so the bite must reach far along them: half a side.
  const bite = ancestorMoving === 'flip' ? '50%'
    : ancestorMoving === 'turn' ? 'calc(var(--tab) / 2 + 1px)'
      : null;
  frameStyle['--bandClip'] = bandClip(self, centerX, centerY, parentCenterX, parentCenterY, bite);

  const { angle, flip } = toCss(upright);
  const previousAngle = useRef(angle);
  const continued = continueAngle(previousAngle.current, angle);
  previousAngle.current = continued;

  const letterStyle: React.CSSProperties = {
    transform: `rotate(${continued}deg) scaleX(${flip ? -1 : 1})`,
  };

  return (
    <div className={pieceClasses}>
      <div className="ultraFlipper" style={flipperStyle}>
        <div className={contentsClasses} style={frameStyle as React.CSSProperties}>
          {subgrid.content.map((row, r) => (
            <div key={r} className="ultraRow">
              {row.map((cell, c) => {
                const isCenter = r === 1 && c === 1;
                const isSide = (r === 1) !== (c === 1);
                const isBlank = cell === '.';
                const classes = 'ultraCell'
                  + (isBlank ? ' blank' : '')
                  + (isSide && interactive ? ' side' : '');
                return (
                  <div
                    key={c}
                    className={classes}
                    onClick={isSide && interactive ? () => flipSubgridByCell(g, q, s, r, c) : undefined}
                  >
                    {isCenter && interactive &&
                      <RotateButton level="subgrid" upright={upright} onRotate={cw => rotateSubgrid(g, q, s, cw)}/>}
                    {!isCenter && !isBlank &&
                      <div className="ultraLetter" style={letterStyle}>{cell}</div>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default Subgrid;
